import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import { ArrowLeft } from 'lucide-react';
import { partnerService } from '../services/partnerService';
import { cepService } from '../services/cepService';
import { categoryService } from '../services/categoryService';
import { Partner, PartnerFormData, Category } from '../types';
import { formatCNPJ, formatPhone, formatCEP, removeMask, getImageUrl } from '../utils/formatters';
import { maskCNPJ, maskPhone, maskCEP } from '../utils/masks';

export const PartnerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchingCEP, setSearchingCEP] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);

  const [formData, setFormData] = useState<PartnerFormData>({
    logo: null,
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    telefone: '',
    email: '',
    whatsapp: '',
    website: '',
    desconto_percentual: 0,
    observacoes: '',
    ativo: true,
    destaque: false,
    category_ids: [],
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchPartner();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAll(true);
      setCategories(data);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  const fetchPartner = async () => {
    setLoading(true);
    try {
      const partner = await partnerService.getById(Number(id));
      setFormData({
        logo: null,
        razao_social: partner.razao_social || partner.name || '',
        nome_fantasia: partner.nome_fantasia || partner.name || '',
        cnpj: partner.cnpj || '',
        cep: partner.cep || '',
        endereco: partner.endereco || partner.address || '',
        numero: partner.numero || '',
        complemento: partner.complemento || '',
        bairro: partner.bairro || '',
        cidade: partner.cidade || '',
        estado: partner.estado || '',
        telefone: partner.telefone || partner.phone || '',
        email: partner.email || '',
        whatsapp: partner.whatsapp || '',
        website: partner.website || '',
        desconto_percentual: partner.desconto_percentual || partner.discount_percentage || 0,
        observacoes: partner.observacoes || '',
        ativo: partner.ativo ?? partner.active ?? true,
        destaque: Boolean(partner.destaque),
        category_ids: partner.categories?.map((c) => c.id) || [],
      });

      if (partner.logo_url) {
        setLogoPreview(getImageUrl(partner.logo_url) || '');
      }
    } catch (err: any) {
      setError('Erro ao carregar dados do parceiro');
      console.error('Partner fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCEPSearch = async (cep: string) => {
    const cleanCEP = removeMask(cep);
    if (cleanCEP.length !== 8) return;

    setSearchingCEP(true);
    try {
      const data = await cepService.buscarCep(cleanCEP);
      if (data && !data.erro) {
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf,
        }));
      }
    } catch (err) {
      console.error('CEP search error:', err);
    } finally {
      setSearchingCEP(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));

      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Limpar máscaras antes de enviar
      const dataToSubmit = {
        ...formData,
        cnpj: removeMask(formData.cnpj),
        cep: removeMask(formData.cep),
        telefone: removeMask(formData.telefone),
        whatsapp: formData.whatsapp ? removeMask(formData.whatsapp) : '',
        ativo: Boolean(formData.ativo), // Garantir que seja boolean
        destaque: Boolean(formData.destaque),
      };

      if (isEditing) {
        await partnerService.update(Number(id), dataToSubmit);
        setSuccess('Parceiro atualizado com sucesso!');
      } else {
        await partnerService.create(dataToSubmit);
        setSuccess('Parceiro criado com sucesso!');
      }

      setTimeout(() => {
        navigate('/parceiros');
      }, 1500);
    } catch (err: any) {
      console.error('Partner save error:', err);

      // Tratar erros de validação do Laravel
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(`Erro: ${err.message}`);
      } else {
        setError(`Erro ao ${isEditing ? 'atualizar' : 'criar'} parceiro. Por favor, verifique os dados e tente novamente.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    let newValue = value;

    // Aplicar máscaras
    if (name === 'cnpj') {
      newValue = maskCNPJ(value);
    } else if (name === 'telefone' || name === 'whatsapp') {
      newValue = maskPhone(value);
    } else if (name === 'cep') {
      newValue = maskCEP(value);
      if (removeMask(newValue).length === 8) {
        handleCEPSearch(newValue);
      }
    }

    setFormData({
      ...formData,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : newValue,
    });
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/parceiros')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Parceiro' : 'Novo Parceiro'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing
              ? 'Atualize as informações do parceiro'
              : 'Preencha os dados do novo parceiro'}
          </p>
        </div>

        {error && (
          <Alert type="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" className="mb-6">
            {success}
          </Alert>
        )}

        <Card>
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo do Parceiro
              </label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-24 h-24 object-contain border border-gray-300 rounded"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>

            {/* Dados da Empresa */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Dados da Empresa
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Razão Social *"
                  name="razao_social"
                  value={formData.razao_social}
                  onChange={handleChange}
                  required
                  placeholder="Nome oficial da empresa"
                />

                <Input
                  label="Nome Fantasia *"
                  name="nome_fantasia"
                  value={formData.nome_fantasia}
                  onChange={handleChange}
                  required
                  placeholder="Nome pelo qual é conhecido"
                />

                <Input
                  label="CNPJ *"
                  name="cnpj"
                  value={formData.cnpj}
                  onChange={handleChange}
                  required
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />

                <Input
                  label="Desconto Percentual * (%)"
                  name="desconto_percentual"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.desconto_percentual}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 10.5"
                />
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Endereço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="CEP *"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  required
                  placeholder="00000-000"
                  maxLength={9}
                  disabled={searchingCEP}
                />

                <div className="md:col-span-2">
                  <Input
                    label="Endereço *"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleChange}
                    required
                    placeholder="Rua, Avenida, etc."
                    disabled={searchingCEP}
                  />
                </div>

                <Input
                  label="Número *"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  required
                  placeholder="123"
                />

                <Input
                  label="Complemento"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                  placeholder="Sala, Andar, etc."
                />

                <Input
                  label="Bairro *"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  required
                  placeholder="Nome do bairro"
                  disabled={searchingCEP}
                />

                <Input
                  label="Cidade *"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  required
                  placeholder="Nome da cidade"
                  disabled={searchingCEP}
                />

                <Input
                  label="Estado *"
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  required
                  placeholder="UF"
                  maxLength={2}
                  disabled={searchingCEP}
                />
              </div>
            </div>

            {/* Contato */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informações de Contato
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Telefone *"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                  placeholder="(00) 0000-0000"
                  maxLength={15}
                />

                <Input
                  label="WhatsApp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />

                <Input
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="contato@parceiro.com.br"
                />

                <Input
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.parceiro.com.br"
                />
              </div>
            </div>

            {/* Categorias */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Categorias</h3>
              <p className="text-sm text-gray-500 mb-3">
                Definem em quais cards de filtro este parceiro aparece na área do associado.
              </p>
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nenhuma categoria ativa cadastrada. Crie categorias em "Categorias" no menu.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.category_ids.includes(category.id)}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            category_ids: e.target.checked
                              ? [...prev.category_ids, category.id]
                              : prev.category_ids.filter((id) => id !== category.id),
                          }));
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {category.nome}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                rows={4}
                placeholder="Informações adicionais sobre o parceiro..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="ativo"
                name="ativo"
                checked={formData.ativo}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="ativo" className="ml-2 text-sm font-medium text-gray-900">
                Parceiro Ativo
              </label>
            </div>

            {/* Destaque */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="destaque"
                name="destaque"
                checked={formData.destaque}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="destaque" className="ml-2 text-sm font-medium text-gray-900">
                Parceiro em Destaque
              </label>
              <span className="ml-2 text-xs text-gray-500">
                (exibido na seção "Parceiros em Destaque" da área do associado)
              </span>
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-6 border-t">
              <Button
                type="submit"
                disabled={loading || searchingCEP}
              >
                {loading
                  ? isEditing
                    ? 'Salvando...'
                    : 'Criando...'
                  : isEditing
                  ? 'Salvar Alterações'
                  : 'Criar Parceiro'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/parceiros')}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
